import { AfterViewInit, Component, DestroyRef, inject, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { filter } from 'rxjs';
import { GroupedNav, NavItem, OrderAuditsRepositoryService, OrderRepositoryService } from 'reg-hub-common';
import { ConfigurationState } from 'reg-hub-common';
import { OrdersAuditsHubService } from '../../../services/hubs/audits-hub/order-audits-hub.service';
import { OrderMessagesHubService } from '../../../services/hubs/order-messages-hub/order-messages-hub.service';

@Component({
    selector: 'app-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SideMenuComponent implements OnInit, AfterViewInit {
  readonly navLabelKeys: Record<string, string> = { 'Workflow': 'sideMenu.workflow', 'Insight': 'sideMenu.insight', 'Configuration': 'sideMenu.configuration', 'Financial': 'sideMenu.financial', 'Dashboard': 'sideMenu.dashboard', 'Orders': 'sideMenu.orders', 'Hub Report Management': 'sideMenu.hubReportManagement', 'Hub Report Management V2': 'sideMenu.hubReportManagementV2', 'Messages': 'sideMenu.messages', 'Audit Queue': 'sideMenu.auditQueue', 'Alerts': 'sideMenu.alerts', 'Batches': 'sideMenu.batches', 'Consortium': 'sideMenu.consortium', 'Reports': 'sideMenu.reports', 'Logs': 'sideMenu.logs', 'Corporations': 'sideMenu.corporations', 'Users': 'sideMenu.users', 'Integrations': 'sideMenu.integrations', 'API Clients': 'sideMenu.apiClients', 'Attestations': 'sideMenu.attestations', 'System': 'sideMenu.system', 'Pricing': 'sideMenu.pricing', 'Invoicing': 'sideMenu.invoicing' };
  @Input() isSidenavOpen: boolean = true;

  @ViewChild(ToastContainerDirective, { static: true }) toastContainer!: ToastContainerDirective;

  selectedItem = '';
  pendingAuditCount: number = 0;
  unreadMessageCount: number = 0;

  private destroyRef = inject(DestroyRef);

  // This will come from a DB call to which nav items a user is allowed to see
  // navItems: NavItem[] = [
  //   { label: 'Dashboard', icon: 'home', link: '/dashboard' },
  //   { label: 'Orders', icon: 'search', link: '/orders' },
  //   { label: 'Batches', icon: 'library_books', link: '/batches' },
  //   { label: 'Corporations', icon: 'apartment', link: '/corporations' },
  //   { label: 'Pricing', icon: 'attach_money', link: '/pricing' },
  //   { label: 'Invoicing', icon: 'payments', link: '/invoices' },
  //   { label: 'Reports', icon: 'assessment', link: '/reports' },
  //   { label: 'Audit Queue', icon: 'zoom_in', link: '/order-audits' },
  //   { label: 'Messages', icon: 'message', link: '/messages' },
  //   { label: 'Logs', icon: 'book', link: '/logs' },
  //   { label: 'Integrations', icon: 'handshake', link: '/integrations' },
  //   { label: 'API Clients', icon: 'webhook', link: '/clients' },
  //   { label: 'System', icon: 'settings', link: '/system' },
  //   { label: 'Consortium', icon: 'experiment', link: '/consortium' },
  //   { label: 'Attestation Setup', icon: 'experiment', link: '/attestation-setup' }
  // ];

  navGroups: GroupedNav[] = [
    {
      label: 'Workflow',
      items: [
        { label: 'Dashboard', icon: 'home', link: '/dashboard' },
        { label: 'Orders', icon: 'search', link: '/orders' },
        { label: 'Hub Report Management', icon: 'graph_5', link: '/hubreports' }, //TODOUBO2AFTERQA: remove this
        { label: 'Hub Report Management V2', icon: 'graph_5', link: '/hubreportsv2' }, //TODOUBO2AFTERQA: rename this and fix routing
        { label: 'Messages', icon: 'message', link: '/messages' },
        { label: 'Audit Queue', icon: 'zoom_in', link: '/order-audits' },
        { label: 'Alerts', icon: 'error', link: '/alerts' },
        { label: 'Batches', icon: 'library_books', link: '/batches' },
        { label: 'Consortium', icon: 'experiment', link: '/consortium' },
      ],
    },
    {
      label: 'Insight',
      items: [
        { label: 'Reports', icon: 'assessment', link: '/reports' },
        { label: 'Logs', icon: 'book', link: '/logs' },
      ],
    },
    {
      label: 'Configuration',
      items: [
        { label: 'Corporations', icon: 'apartment', link: '/corporations' },
        { label: 'Users', icon: 'person', link: '/search-users' },
        { label: 'Integrations', icon: 'handshake', link: '/integrations' },
        { label: 'API Clients', icon: 'webhook', link: '/clients' },
        { label: 'Attestations', icon: 'account_child_invert', link: '/attestation-templates' },
        { label: 'System', icon: 'settings', link: '/system' },
      ],
    },
    {
      label: 'Financial',
      items: [
        { label: 'Pricing', icon: 'attach_money', link: '/pricing' },
        { label: 'Invoicing', icon: 'payments', link: '/invoices' },
      ],
    },
  ];

  constructor(
    private messageService: ToastrService,
    private router: Router,
    //private configurationState: ConfigurationState,
    private ordersAuditService: OrderAuditsRepositoryService,
    private ordersHubService: OrdersAuditsHubService,
    private ordersRepo: OrderRepositoryService,
    private orderMessagesHubService: OrderMessagesHubService) { }

  ngOnInit(): void {
    this.ordersHubService.connect();
    this.orderMessagesHubService.connect();
    this.messageService.overlayContainer = this.toastContainer;

    this.router.events
      .pipe(filter(event => event instanceof NavigationStart), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.messageService.clear());
  }

  ngAfterViewInit() {
    this.ordersAuditService.getPendingAuditCount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this.ordersHubService.setInitialAuditPendingCount(count);
      this.monitorPendingAuditCount();
    });

    this.ordersRepo.getUnreadAdminMessageCount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this.orderMessagesHubService.setInitialUnreadMessageCount(count);
      this.monitorUnreadMessageCount();
    });
  }

  ngOnDestroy() {
    this.ordersHubService.leavePendingAuditsGroup();
  }

  getBadgeValue(label: string): number | '' {
    if (label === 'Audit Queue') {
      return this.pendingAuditCount;
    }
    if (label === 'Messages') {
      return this.unreadMessageCount;
    }
    return '';
  }
  
  shouldHideBadge(label: string): boolean {
    if (label === 'Audit Queue') {
      return this.pendingAuditCount < 1;
    }
    if (label === 'Messages') {
      return this.unreadMessageCount < 1;
    }
    return true;
  }

  handleClick(selectedItem: NavItem) {
    this.selectedItem = selectedItem.label;
  }

  monitorPendingAuditCount() {
    this.ordersHubService.joinPendingAuditsGroup();

    this.ordersHubService.pendingAuditCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((count) => {
      //Observable
      this.pendingAuditCount = count;
    });
  }

  monitorUnreadMessageCount() {
    this.orderMessagesHubService.unreadMessageCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((count) => {
      //Observable
      this.unreadMessageCount = count;
    });
  }
}
